import { useState, useEffect, useRef, useMemo, useCallback } from "react";

// ─── QUESTION BANK ────────────────────────────────────────────────────────────
const QUESTIONS = [
  // ── SET 1 (Q1–Q30) ──
  {
    id:1, text:'Which of the following is a "Standardized" feature of an exchange-traded option contract?',
    options:["The commission rate.","The specific strike prices available.","The market price (premium) of the option.","The identity of the counterparty."],
    correct:1, topic:"Contract Features", difficulty:"easy",
    rationale:"Exchanges standardize the strike prices, expiration dates, and multipliers. The premium is determined by supply and demand, and the counterparty is replaced by the clearinghouse via novation.",
  },
  {
    id:2, text:'An investor who "Writes" a call option is best described as having:',
    options:["The right to buy the underlying asset.","The obligation to buy the underlying asset.","The right to sell the underlying asset.","The obligation to sell the underlying asset."],
    correct:3, topic:"Rights & Obligations", difficulty:"easy",
    rationale:"Writers are sellers. A call writer receives a premium in exchange for the obligation to deliver (sell) the stock if the holder exercises.",
  },
  {
    id:3, text:"An investor buys 3 S&P/TSX 60 Index Call options. The multiplier is $200. If the option premium is quoted at $12.50, what is the total cost for the 3 contracts (excluding commissions)?",
    options:["$37.50","$2,500.00","$7,500.00","$37,500.00"],
    correct:2, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Cost = Premium × Multiplier × Contracts. $12.50 × $200 × 3 = $7,500.",
  },
  {
    id:4, text:"A jewelry manufacturer needs to purchase 500 ounces of gold in six months and is worried the price will rise. To fix their future purchase price today, they should:",
    options:["Sell 5 gold futures contracts.","Buy 5 gold futures contracts (assuming 100 oz per contract).","Buy 5 gold put options.","Sell 5 gold call options."],
    correct:1, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Buying a futures contract (Long) locks in the purchase price. 500 oz ÷ 100 oz per contract = 5 contracts.",
  },
  {
    id:5, text:'A "European-style" option differs from an "American-style" option because it can be exercised:',
    options:["Only in European markets.","Only on the expiration date.","At any time up to and including the expiration date.","Only if the underlying stock is trading in Euros."],
    correct:1, topic:"Contract Features", difficulty:"easy",
    rationale:"European-style options restrict exercise to the expiration date only. Most index options in Canada are European-style.",
  },
  {
    id:6, text:'Which of the following derivatives is considered "Over-the-Counter" (OTC)?',
    options:["An S&P/TSX 60 Index Future.","A Canadian Bank Equity Option.","A customized Forward contract between two banks.","A Government of Canada Bond Future."],
    correct:2, topic:"Futures & Forwards", difficulty:"easy",
    rationale:"Forward contracts are private, non-standardized agreements traded off-exchange (OTC).",
  },
  {
    id:7, text:'"Open Interest" in the options market is defined as:',
    options:["The total number of shares traded in a day.","The total number of contracts that have been closed out.","The total number of outstanding contracts that have not yet been settled by an offsetting trade or exercise.","The number of investors currently looking to buy a specific call."],
    correct:2, topic:"Market Mechanics", difficulty:"hard",
    rationale:"Open interest represents the total 'live' contracts in the market.",
  },
  {
    id:8, text:'In an "In-the-Money" (ITM) Put option:',
    options:["The stock price is higher than the strike price.","The stock price is lower than the strike price.","The strike price is equal to the stock price.","The premium is zero."],
    correct:1, topic:"Moneyness & Intrinsic Value", difficulty:"easy",
    rationale:"A put is ITM if you can sell the stock (via the option) for more than its current market price.",
  },
  {
    id:9, text:"A stock is trading at $54. A Call option with a strike price of $50 is trading for a premium of $5.50. What is the Intrinsic Value of this option?",
    options:["$1.50","$4.00","$5.50","$0.00"],
    correct:1, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Intrinsic Value (Call) = Stock Price − Strike Price. $54 − $50 = $4.00.",
  },
  {
    id:10, text:'"Contango" is a market condition where:',
    options:["The spot price is higher than the futures price.","The futures price is higher than the spot price.","The options have no time value.","The volatility is extremely low."],
    correct:1, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Contango typically occurs when there are costs associated with carrying the asset (storage, interest).",
  },
  {
    id:11, text:'The "Underlying Asset" of a derivative can be:',
    options:["A stock or an index.","A commodity or a currency.","An interest rate or a bond.","All of the above."],
    correct:3, topic:"Contract Features", difficulty:"easy",
    rationale:"Derivatives can be based on almost any financial variable or physical asset.",
  },
  {
    id:12, text:'When a futures contract is "Marked-to-Market," the gains and losses are settled:',
    options:["Only when the contract expires.","Every month.","Every day.","Only when the investor requests a payout."],
    correct:2, topic:"Settlement", difficulty:"easy",
    rationale:"Daily settlement is a key feature of futures that reduces default risk.",
  },
  {
    id:13, text:"Which of the following would cause a decrease in an option's Time Value?",
    options:["An increase in implied volatility.","The passage of time (approaching expiration).","An increase in the stock price for a call.","A decrease in interest rates."],
    correct:1, topic:"The Greeks", difficulty:"hard",
    rationale:"Time decay (Theta) reduces the time value of an option as it gets closer to expiration.",
  },
  {
    id:14, text:'An investor is "Short" an S&P/TSX 60 Index Future. If the index price falls, the investor\'s margin account will be:',
    options:["Debited (decreased).","Credited (increased).","Closed immediately.","Unaffected until they sell."],
    correct:1, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Short positions profit when prices fall. The daily mark-to-market will result in a credit to the account.",
  },
  {
    id:15, text:'"Cash Settlement" means that at expiration:',
    options:["The investor must deliver physical gold.","The gain or loss is settled by a cash payment rather than delivery of the underlying asset.","The broker pays the client a commission in cash.","The investor must pay the full strike price in cash to get the shares."],
    correct:1, topic:"Settlement", difficulty:"easy",
    rationale:"Many index and financial derivatives are cash-settled for ease of processing.",
  },
  {
    id:16, text:'The "Multiplier" for a Government of Canada 10-year Bond Future (CGB) is based on a notional principal of:',
    options:["$1,000","$10,000","$100,000","$1,000,000"],
    correct:2, topic:"Bond Futures", difficulty:"medium",
    rationale:"Most Canadian bond futures use a $100,000 notional value.",
  },
  {
    id:17, text:"A client sells 10 Crude Oil futures at $80.00. Each contract is for 1,000 barrels. If the price rises to $81.50, what is the daily loss for the client?",
    options:["$1,500","$10,000","$15,000","$81,500"],
    correct:2, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Loss = (New Price − Old Price) × Multiplier × Contracts. ($81.50 − $80.00) × 1,000 × 10 = $1.50 × 10,000 = $15,000.",
  },
  {
    id:18, text:'An "Out-of-the-Money" (OTM) Call option has:',
    options:["Intrinsic value only.","Time value only.","Both intrinsic and time value.","No value at all."],
    correct:1, topic:"Moneyness & Intrinsic Value", difficulty:"easy",
    rationale:"OTM options have zero intrinsic value; their entire premium consists of time value.",
  },
  {
    id:19, text:'"LEAPS" are options that have expiration dates:',
    options:["Only in February of a leap year.","Of less than 30 days.","Of up to three years.","That are decided by the investor."],
    correct:2, topic:"Contract Features", difficulty:"easy",
    rationale:"Long-term Equity Anticipation Securities (LEAPS) are long-dated options.",
  },
  {
    id:20, text:'What happens to "Open Interest" when a buyer who is "Opening" a position trades with a seller who is "Closing" a position?',
    options:["Open interest increases.","Open interest decreases.","Open interest remains unchanged.","Open interest becomes zero."],
    correct:2, topic:"Market Mechanics", difficulty:"hard",
    rationale:"One new contract is created (by the buyer) while one old contract is closed (by the seller), resulting in no net change.",
  },
  {
    id:21, text:'A "Long Put" position is a bet that the underlying stock price will:',
    options:["Rise.","Fall.","Stay the same.","Increase in volatility only."],
    correct:1, topic:"Rights & Obligations", difficulty:"easy",
    rationale:"Puts gain value as the stock price drops below the strike price.",
  },
  {
    id:22, text:'In the "Forward" market, the price is generally determined by:',
    options:["An auction on the exchange floor.","Direct negotiation between the two parties.","The clearinghouse.","The previous day's closing price on the Montreal Exchange."],
    correct:1, topic:"Futures & Forwards", difficulty:"easy",
    rationale:"Forwards are bilateral, private contracts.",
  },
  {
    id:23, text:"A Put option with a strike price of $90 is trading for $12.00. The underlying stock is at $82. What is the Time Value of the option?",
    options:["$4.00","$8.00","$12.00","$0.00"],
    correct:0, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Intrinsic Value = $90 − $82 = $8.00. Time Value = Total Premium − Intrinsic Value = $12.00 − $8.00 = $4.00.",
  },
  {
    id:24, text:'Which of the following describes "Backwardation"?',
    options:["The futures price is lower than the spot price.","The futures price is higher than the spot price.","The option is deep in-the-money.","The stock is undergoing a reverse split."],
    correct:0, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Backwardation often suggests an immediate shortage of the physical commodity.",
  },
  {
    id:25, text:"An investor owns 500 shares of ABC at $60. They sell 5 ABC Call options with a $65 strike. This is called a:",
    options:["Naked Call.","Covered Call.","Bull Spread.","Protective Put."],
    correct:1, topic:"Contract Features", difficulty:"easy",
    rationale:"Since the investor owns the underlying shares to deliver if assigned, the call is 'covered.'",
  },
  {
    id:26, text:'The "Last Trading Day" for an equity option on the Montreal Exchange is typically:',
    options:["The third Friday of the expiration month.","The last business day of the month.","Any day the investor chooses.","The Monday following the third Friday."],
    correct:0, topic:"Contract Features", difficulty:"easy",
    rationale:"This is the standardized expiration cycle for North American equity options.",
  },
  {
    id:27, text:'What is the "Strike Price" of an option?',
    options:["The price at which the option was bought.","The price at which the underlying asset can be bought or sold if the option is exercised.","The current market price of the underlying asset.","The price of the option on the day of expiration."],
    correct:1, topic:"Contract Features", difficulty:"easy",
    rationale:"Also known as the exercise price.",
  },
  {
    id:28, text:"An S&P/TSX 60 Index Future is trading at 1,200. The multiplier is $200. What is the Notional Value of one contract?",
    options:["$1,200","$20,000","$240,000","$1,200,000"],
    correct:2, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Notional Value = Index Level × Multiplier. 1,200 × $200 = $240,000.",
  },
  {
    id:29, text:'A "Futures" contract is a legal obligation to:',
    options:["Buy or sell an asset at a future date at a price determined today.","Buy an asset only if the price goes up.","Pay a commission to the Montreal Exchange.","Sell an asset only if you already own it."],
    correct:0, topic:"Futures & Forwards", difficulty:"easy",
    rationale:"Unlike options (which are rights), futures are obligations for both parties.",
  },
  {
    id:30, text:"Which of the following is NOT a standardized feature of a futures contract?",
    options:["Contract size (quantity).","Quality or grade of the commodity.","Delivery location.","The initial margin amount (as negotiated between the buyer and seller)."],
    correct:3, topic:"Contract Features", difficulty:"medium",
    rationale:"While the exchange sets minimum margin requirements, margin is a performance bond, not a contract feature like size or grade.",
  },
  // ── SET 2 (Q31–Q60) ──
  {
    id:31, text:'The "Writer" of a put option has the:',
    options:["Right to sell the underlying asset.","Obligation to sell the underlying asset.","Right to buy the underlying asset.","Obligation to buy the underlying asset."],
    correct:3, topic:"Rights & Obligations", difficulty:"easy",
    rationale:"A put writer receives a premium for taking on the obligation to buy the stock at the strike price if the holder exercises.",
  },
  {
    id:32, text:"An S&P/TSX 60 Index Option (SXO) is generally described as:",
    options:["Physical delivery and American-style.","Cash-settled and European-style.","Physical delivery and European-style.","Cash-settled and American-style."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"Most broad-based index options settle in cash and can only be exercised at expiry.",
  },
  {
    id:33, text:"An investor is Long 5 Gold futures (100 oz per contract) at $2,150. If the price of gold moves to $2,162, what is the change in the account's equity?",
    options:["$1,200 increase.","$6,000 increase.","$12,000 increase.","$6,000 decrease."],
    correct:1, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Profit = ($2,162 − $2,150) × 100 oz × 5 contracts = $12 × 500 = $6,000.",
  },
  {
    id:34, text:'A "Binary" or "Digital" option is a type of derivative where the payoff is:',
    options:["Unlimited.","Based on the difference between the strike and the spot.","A fixed amount or zero.","Paid in cryptocurrency only."],
    correct:2, topic:"Contract Features", difficulty:"medium",
    rationale:"Binary options pay a fixed sum if the condition is met, regardless of how far 'in the money' the option is.",
  },
  {
    id:35, text:"A company expects to receive 1,000,000 USD in three months and wants to lock in the exchange rate to Canadian Dollars (CAD). They should:",
    options:["Buy USD/CAD futures.","Sell USD/CAD futures.","Buy USD call options.","Sell CAD put options."],
    correct:1, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"To hedge the value of a currency you will receive, you sell that currency in the futures market to lock in the conversion rate.",
  },
  {
    id:36, text:'The "Time Value" of an option is typically highest when the option is:',
    options:["Deep in-the-money.","Deep out-of-the-money.","At-the-money.","At the point of expiration."],
    correct:2, topic:"Moneyness & Intrinsic Value", difficulty:"medium",
    rationale:"ATM options have the most uncertainty regarding their eventual outcome, which maximizes the extrinsic (time) value.",
  },
  {
    id:37, text:'Which of the following is an advantage of "Exchange-Traded" derivatives over "OTC" derivatives?',
    options:["Customization of contract sizes.","Privacy of the transaction.","Elimination of counterparty credit risk via the clearinghouse.","Ability to trade on weekends."],
    correct:2, topic:"Market Mechanics", difficulty:"medium",
    rationale:"The CDCC guarantees the performance of exchange-traded contracts, whereas OTC contracts depend on the other party's credit.",
  },
  {
    id:38, text:'"Basis" in the futures market is mathematically defined as:',
    options:["Futures Price − Spot Price.","Spot Price − Futures Price.","Strike Price − Stock Price.","Intrinsic Value + Time Value."],
    correct:1, topic:"Futures & Forwards", difficulty:"hard",
    rationale:"Basis is the difference between the current cash (spot) price and the futures price.",
  },
  {
    id:39, text:"A client holds 10 Canadian 10-year Bond Futures (CGB). The contract price is 124.50. If the price moves to 125.10, what is the gain? (Each 0.01 point = $10 per contract).",
    options:["$600","$6,000","$10,000","$60,000"],
    correct:1, topic:"Numerical & Valuation", difficulty:"hard",
    rationale:"Change in points = 125.10 − 124.50 = 0.60 points. Since 0.01 = $10, 1.00 = $1,000. Gain = 0.60 × $1,000 × 10 contracts = $6,000.",
  },
  {
    id:40, text:'An option that is "At-the-Money" has a Delta of approximately:',
    options:["0.00","0.50","1.00","−1.00"],
    correct:1, topic:"The Greeks", difficulty:"medium",
    rationale:"An ATM option has roughly a 50/50 chance of ending in or out of the money, so its delta is near 0.50.",
  },
  {
    id:41, text:'The "Premium" of an option is paid by the:',
    options:["Seller to the Buyer.","Buyer to the Seller.","Clearinghouse to the Broker.","Broker to the Exchange."],
    correct:1, topic:"Rights & Obligations", difficulty:"easy",
    rationale:"The buyer pays for the 'right,' while the seller receives it for the 'obligation.'",
  },
  {
    id:42, text:'A "Forward" contract is typically settled:',
    options:["Daily through mark-to-market.","Only at the end of the contract term.","By the Montreal Exchange.","Using standardized multipliers."],
    correct:1, topic:"Settlement", difficulty:"medium",
    rationale:"Forwards do not usually have daily margin/settlement like futures; everything happens at maturity.",
  },
  {
    id:43, text:"An investor believes that a specific stock will be extremely volatile but is unsure of the direction. The most appropriate strategy type would be:",
    options:["Selling a straddle.","Buying a straddle.","Buying the underlying stock.","Selling a covered call."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"Buying a straddle (Call + Put) profits from large price moves in either direction.",
  },
  {
    id:44, text:'Which of the following describes "Intrinsic Value" for a Call option?',
    options:["Max (0, Strike − Stock).","Max (0, Stock − Strike).","Premium − Time Value.","Both B and C are correct."],
    correct:3, topic:"Moneyness & Intrinsic Value", difficulty:"medium",
    rationale:"Intrinsic value is the 'built-in' profit of the option (S − K) or simply what is left of the premium after subtracting time value.",
  },
  {
    id:45, text:"An investor buys 5 Silver futures at $24.50/oz. The multiplier is 5,000 oz. The Initial Margin is $10,000 per contract. What is the total margin the investor must deposit?",
    options:["$10,000","$50,000","$122,500","$500,000"],
    correct:1, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Total Margin = Initial Margin per contract × Number of contracts. $10,000 × 5 = $50,000.",
  },
  {
    id:46, text:'A "Swap" is a derivative where two parties exchange:',
    options:["Ownership of two different companies.","Cash flows from two different financial instruments.","Shares of stock for physical commodities.","Voting rights in a corporation."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"Common swaps include interest rate swaps (fixed for floating) or currency swaps.",
  },
  {
    id:47, text:'"Physical Delivery" in the futures market means:',
    options:["The investor must go to the exchange to pick up a check.","The actual underlying asset (e.g., wheat, bonds) must be transferred from the seller to the buyer.","The broker delivers a paper certificate to the client's home.","All gains are paid in cash."],
    correct:1, topic:"Settlement", difficulty:"easy",
    rationale:"While rare for speculators, many commodity and bond futures require actual delivery of the asset.",
  },
  {
    id:48, text:"Which Greek measures the sensitivity of an option's premium to changes in Implied Volatility?",
    options:["Delta.","Gamma.","Theta.","Vega."],
    correct:3, topic:"The Greeks", difficulty:"hard",
    rationale:"Vega is the measure of risk regarding volatility shifts.",
  },
  {
    id:49, text:"An option that has 90 days to expiration will generally have ______ time value than an identical option with 30 days to expiration.",
    options:["Less.","More.","The same amount of.","Zero."],
    correct:1, topic:"Moneyness & Intrinsic Value", difficulty:"easy",
    rationale:"More time allows for a greater chance of the stock moving favorably, thus it costs more.",
  },
  {
    id:50, text:"ABC Stock is at $100. A client sells one $105 Call for $3.00. What is the Breakeven price for the client at expiration?",
    options:["$100","$102","$105","$108"],
    correct:3, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"For a call seller, breakeven is Strike + Premium. $105 + $3 = $108.",
  },
  {
    id:51, text:"Which of the following is NOT a feature of a standardized option contract?",
    options:["The name of the underlying security.","The expiration month.","The number of shares (multiplier).","The volatility of the underlying stock."],
    correct:3, topic:"Contract Features", difficulty:"medium",
    rationale:"Volatility is a market-driven variable used to price the option, not a fixed feature of the contract.",
  },
  {
    id:52, text:'"Novation" occurs when:',
    options:["A new CEO is appointed to the Montreal Exchange.","The clearinghouse interposes itself between the buyer and seller.","An option is exercised early.","A stock undergoes a reverse split."],
    correct:1, topic:"Market Mechanics", difficulty:"medium",
    rationale:"Novation legally replaces the original contract with two new ones with the CDCC.",
  },
  {
    id:53, text:"A speculator believes the Canadian Dollar will weaken against the US Dollar. To express this using features of the futures market, they should:",
    options:['Go "Long" CAD futures.','Go "Short" CAD futures.',"Buy CAD call options.","Sell USD put options."],
    correct:1, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Shorting the CAD future means you profit if the value of the CAD falls.",
  },
  {
    id:54, text:'"Open Interest" of a specific option class is reported:',
    options:["Every hour.","Once a day, typically before the market opens.","Only on expiration Friday.","Once a month."],
    correct:1, topic:"Market Mechanics", difficulty:"medium",
    rationale:"Exchanges update open interest figures overnight after all trades have been cleared.",
  },
  {
    id:55, text:"A Put option has a strike of $50 and the stock is at $45. The total premium is $7.00. What is the Intrinsic Value?",
    options:["$2.00","$5.00","$7.00","$0.00"],
    correct:1, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Intrinsic Value (Put) = Strike − Stock. $50 − $45 = $5.00.",
  },
  {
    id:56, text:'A "Hedge" is a derivative position taken specifically to:',
    options:["Generate aggressive profits.","Reduce or eliminate the risk of an existing physical position.","Avoid paying taxes.","Manipulate the price of a stock."],
    correct:1, topic:"Futures & Forwards", difficulty:"easy",
    rationale:"Hedging is an insurance-like use of derivatives.",
  },
  {
    id:57, text:'"Settlement" in the futures market refers to:',
    options:["The process of arguing about a trade price.","The daily exchange of cash to reflect gains and losses.","Closing the brokerage account.","Signing the KYC form."],
    correct:1, topic:"Settlement", difficulty:"easy",
    rationale:"This specifically refers to the mark-to-market and variation margin process.",
  },
  {
    id:58, text:"An option's Gamma is highest when the option is:",
    options:["Deeply out-of-the-money.","Deeply in-the-money.","At-the-money and near expiration.","At-the-money and far from expiration."],
    correct:2, topic:"The Greeks", difficulty:"hard",
    rationale:"Delta changes most rapidly for ATM options as they approach expiry (the 'clinch' point).",
  },
  {
    id:59, text:'A "Synthetic" position is one that:',
    options:["Is traded only in simulated accounts.","Replicates the risk/reward profile of another instrument using different components.","Uses only digital currencies as collateral.","Is prohibited by CIRO."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"For example, a 'Synthetic Long Stock' is created using a Long Call and a Short Put.",
  },
  {
    id:60, text:'The "Last Trading Day" for a futures contract is:',
    options:["Always the same as equity options.","Specified in the standardized contract terms for that specific asset.","The day the investor decides to exit.","The last day of the year."],
    correct:1, topic:"Contract Features", difficulty:"easy",
    rationale:"Different futures (Gold vs. Oil vs. Bonds) have different standardized monthly cycles.",
  },
  // ── SET 3 (Q61–Q90) ──
  {
    id:61, text:'A "Qualified Investment" for a Registered Plan (like an RRSP) includes which type of derivative?',
    options:["Uncovered (Naked) Calls.","Short Futures positions.","Long Call and Put options.","Uncovered (Naked) Puts."],
    correct:2, topic:"Contract Features", difficulty:"medium",
    rationale:"Only 'Long' positions or 'Covered' writing are typically permitted in registered accounts to limit risk to the account's capital.",
  },
  {
    id:62, text:'Which feature of a futures contract is designed to prevent "Limit Up" and "Limit Down" moves from causing systemic panic?',
    options:["Initial Margin.","Price Limits (Circuit Breakers).","Open Interest caps.","Novation."],
    correct:1, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Daily price limits temporarily halt trading if a commodity moves too far, too fast, allowing the market to 'cool off.'",
  },
  {
    id:63, text:'An investor holds a bond future with a "Conversion Factor" of 0.85. If the futures settlement price is 120.00, what is the Invoice Price for the delivery (excluding accrued interest)?',
    options:["$100.00","$102.00","$120.00","$141.17"],
    correct:1, topic:"Numerical & Valuation", difficulty:"hard",
    rationale:"Invoice Price = Futures Price × Conversion Factor. 120.00 × 0.85 = 102.00 (or $102,000 for a $100k contract).",
  },
  {
    id:64, text:"An equity option on ABC stock is adjusted for a 2-for-1 split. If the original strike was $80 and the contract was for 100 shares, the new strike and contract size are:",
    options:["$40 strike; 100 shares (but 2 contracts).","$160 strike; 50 shares.","$40 strike; 200 shares.","$80 strike; 200 shares."],
    correct:0, topic:"Contract Features", difficulty:"hard",
    rationale:"In a standard split, the number of contracts increases (doubles) and the strike price is reduced (halved).",
  },
  {
    id:65, text:'"Rho" measures an option\'s sensitivity to changes in:',
    options:["Dividends.","Volatility.","The underlying stock price.","Interest rates."],
    correct:3, topic:"The Greeks", difficulty:"medium",
    rationale:"Rho is the Greek associated with interest rate risk.",
  },
  {
    id:66, text:'The "Cheapest-to-Deliver" (CTD) feature is most common in which derivative?',
    options:["S&P/TSX 60 Index Futures.","Gold Options.","Government of Canada Bond Futures (CGB).","Crude Oil Forwards."],
    correct:2, topic:"Bond Futures", difficulty:"medium",
    rationale:"Bond futures allow the seller to choose from a basket of deliverable bonds; they will naturally pick the one that is cheapest to provide.",
  },
  {
    id:67, text:'An investor is "Short" a call with a Delta of 0.75. If the stock price rises by $1.00, the investor\'s position will:',
    options:["Gain $0.75.","Lose $0.75.","Gain $1.00.","Lose $1.00."],
    correct:1, topic:"The Greeks", difficulty:"hard",
    rationale:"Short call holders have negative delta. If the stock rises ($+1.00), the value of the option rises, which is a loss for the seller (−$0.75).",
  },
  {
    id:68, text:'A "Position Limit" on the Montreal Exchange is calculated based on:',
    options:["The total value of the account.","The number of contracts on the same side of the market (Long Calls + Short Puts).","The number of shares the investor owns in their cash account.","The daily trading volume of the firm."],
    correct:1, topic:"Market Mechanics", difficulty:"medium",
    rationale:"Regulators aggregate positions that share the same directional risk (bullish vs. bearish).",
  },
  {
    id:69, text:'A "Straddle" consists of buying a $100 Call for $4.00 and a $100 Put for $3.50. What is the "Upper Breakeven" price?',
    options:["$100.00","$104.00","$103.50","$107.50"],
    correct:3, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Total Cost = $4.00 + $3.50 = $7.50. Upper Breakeven = Strike + Total Premium = $100 + $7.50 = $107.50.",
  },
  {
    id:70, text:'Which feature defines a "Future" as having lower counterparty risk than a "Forward"?',
    options:["Customized contract sizes.","Privacy of terms.","The presence of a Central Counterparty (the CDCC).","No margin requirements."],
    correct:2, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"The clearinghouse acts as the guarantor of every trade through novation.",
  },
  {
    id:71, text:'"Delta" is often used as a proxy for:',
    options:["The probability that an option will expire in-the-money.","The interest rate on a margin loan.","The storage cost of a commodity.","The time remaining until expiration."],
    correct:0, topic:"The Greeks", difficulty:"medium",
    rationale:"While not statistically perfect, a 0.50 Delta is widely considered a '50% chance' of the option finishing ITM.",
  },
  {
    id:72, text:'An option is "Deep Out-of-the-Money" if its Delta is:',
    options:["0.50","0.90","0.10","1.00"],
    correct:2, topic:"The Greeks", difficulty:"medium",
    rationale:"A very low delta (near 0) indicates the option has a very low probability of ever reaching its strike price.",
  },
  {
    id:73, text:"A silver miner expects to produce 25,000 oz of silver in 3 months. To protect against falling prices, they should:",
    options:["Sell 5 silver futures (assuming 5,000 oz per contract).","Buy 5 silver futures.","Buy 5 silver call options.","Sell 5 silver put options."],
    correct:0, topic:"Futures & Forwards", difficulty:"medium",
    rationale:"Producers use a 'Short Hedge' (selling futures) to lock in the selling price of their future production.",
  },
  {
    id:74, text:'The "Exercise Price" is standardized by the exchange to occur in "intervals." These intervals are generally smaller for:',
    options:["High-priced stocks.","Low-priced stocks.","Volatile commodities.","Foreign currencies."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"For cheaper stocks (e.g., under $5), strikes may occur every $0.50, whereas for expensive stocks ($200+), they may occur every $5 or $10.",
  },
  {
    id:75, text:'"Variation Margin" is the amount of cash that must be deposited:',
    options:["To open a new position.","To restore a margin account to its 'Initial' level after a loss.","To pay the RR's commission.","To cover the interest on a loan."],
    correct:1, topic:"Settlement", difficulty:"medium",
    rationale:"Variation margin is the cash needed to bring an account back from a maintenance deficiency.",
  },
  {
    id:76, text:"An investor sells a $90 Put for $4.00. What is the Maximum Profit per contract?",
    options:["$90","$400","$8,600","Unlimited"],
    correct:1, topic:"Numerical & Valuation", difficulty:"easy",
    rationale:"The seller's maximum profit is limited to the premium received. $4.00 × 100 = $400.",
  },
  {
    id:77, text:'"Mark-to-Market" settlement occurs at which price?',
    options:["The opening price of the day.","The high price of the day.","The daily settlement price (usually a weighted average near the close).","The price at which the investor entered the trade."],
    correct:2, topic:"Settlement", difficulty:"medium",
    rationale:"This ensures all accounts are valued consistently against the same benchmark at the end of the day.",
  },
  {
    id:78, text:'A "Synthetic Short Put" has the same risk/reward profile as:',
    options:["A Long Call.","A Covered Call.","A Long Put.","A Naked Call."],
    correct:1, topic:"Contract Features", difficulty:"hard",
    rationale:"Long Stock + Short Call = Synthetic Short Put. Both have capped upside and downside risk equivalent to owning the stock.",
  },
  {
    id:79, text:'The "Expiration Cycle" for an equity option is traditionally:',
    options:["Monthly.","Quarterly.","Semi-annually.","Weekly."],
    correct:0, topic:"Contract Features", difficulty:"easy",
    rationale:"Standard equity options have monthly expirations, though 'weeklies' are increasingly common.",
  },
  {
    id:80, text:'"Negative Gamma" is a characteristic of which position?',
    options:["Long Call.","Long Put.","Short Call.","Long Straddle."],
    correct:2, topic:"The Greeks", difficulty:"hard",
    rationale:"Option Writers (sellers) have negative Gamma, meaning their directional risk (Delta) accelerates against them as the stock moves.",
  },
  {
    id:81, text:'Which derivative is "Private" and not reported to a central exchange?',
    options:["SXO Index Option.","Forward Contract.","SXF Index Future.","CGB Bond Future."],
    correct:1, topic:"Futures & Forwards", difficulty:"easy",
    rationale:"Forwards are bilateral agreements between two private parties.",
  },
  {
    id:82, text:"An investor is Long an S&P/TSX 60 Index Call at 1,200 strike. If the index is at 1,215 at expiry, how much cash will the investor receive (Multiplier = $200)?",
    options:["$15","$200","$3,000","$240,000"],
    correct:2, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Cash settlement = (Index − Strike) × Multiplier. (1,215 − 1,200) × $200 = $3,000.",
  },
  {
    id:83, text:'"Theta" is most detrimental (negative) to:',
    options:["Option Writers.","Option Buyers.","Futures Sellers.","Stock Owners."],
    correct:1, topic:"The Greeks", difficulty:"medium",
    rationale:"Time decay 'erodes' the premium that a buyer paid.",
  },
  {
    id:84, text:'A "Vertical Spread" involves two options of the same class and expiry but different:',
    options:["Multipliers.","Underlying stocks.","Strike prices.","Styles (American vs. European)."],
    correct:2, topic:"Contract Features", difficulty:"medium",
    rationale:"Vertical spreads appear 'vertically' on an options chain because only the strike price changes.",
  },
  {
    id:85, text:"A trader believes volatility will increase but doesn't want to bet on a specific direction. They should buy a:",
    options:["Bull Spread.","Covered Call.","Straddle.","Protective Put."],
    correct:2, topic:"Contract Features", difficulty:"medium",
    rationale:"Straddles are directionally neutral but profit from large volatility moves.",
  },
  {
    id:86, text:'The "Daily Settlement Price" is determined by:',
    options:["The Montreal Exchange.","The CDCC.","The individual brokerage firm.","The provincial regulator."],
    correct:0, topic:"Settlement", difficulty:"medium",
    rationale:"The exchange calculates the price used for mark-to-market.",
  },
  {
    id:87, text:'"In-the-Money" (ITM) for a Call option means:',
    options:["Strike > Stock.","Stock > Strike.","Premium > Intrinsic Value.","Multiplier > 100."],
    correct:1, topic:"Moneyness & Intrinsic Value", difficulty:"easy",
    rationale:"A call is ITM if the holder can buy the stock for less than its current market value.",
  },
  {
    id:88, text:"An option has a Vega of 0.15. If Implied Volatility rises by 5%, the option's premium will:",
    options:["Fall by $0.15.","Rise by $0.15.","Rise by $0.75.","Stay the same."],
    correct:2, topic:"Numerical & Valuation", difficulty:"hard",
    rationale:"Change = Vega × Change in Vol. 0.15 × 5 = $0.75.",
  },
  {
    id:89, text:'"Last Trading Day" for MX futures is often:',
    options:["The third Friday of the month.","The business day prior to the expiration date.","The same as the delivery date.","Any day the trader chooses."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"Standardized rules ensure all participants know when they must exit or face delivery.",
  },
  {
    id:90, text:"Which of the following is NOT a standardized feature of a bond future?",
    options:["The notional coupon rate (e.g., 6%).","The contract size ($100,000).","The delivery months.","The identity of the 'Cheapest-to-Deliver' bond on the day of trade."],
    correct:3, topic:"Bond Futures", difficulty:"hard",
    rationale:"The CTD bond is determined by market prices at the time of delivery, not pre-set by the contract.",
  },
  // ── SET 4 (Q91–Q100) ──
  {
    id:91, text:"Which Greek describes the sensitivity of an option's Delta to a change in the underlying stock price?",
    options:["Theta","Gamma","Vega","Rho"],
    correct:1, topic:"The Greeks", difficulty:"medium",
    rationale:"Gamma is the 'Delta of the Delta.' It measures how much the Delta moves for every $1 move in the stock.",
  },
  {
    id:92, text:"An investor holds 10 Government of Canada 5-year Bond Futures (CGF). The contract price is 115.00. A Tick is 0.01 points and is worth $10.00 per contract. If the price moves to 115.08, what is the dollar gain?",
    options:["$80","$800","$1,150","$8,000"],
    correct:1, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Price moved 8 ticks (115.08 − 115.00). Gain = 8 ticks × $10.00/tick × 10 contracts = $800.",
  },
  {
    id:93, text:'If a company issues a "Special Cash Dividend" (as opposed to a regular quarterly dividend), the Montreal Exchange typically:',
    options:["Increases the strike price of the options.","Decreases the strike price of the options.","Cancels all existing option contracts.","Does nothing, as dividends only affect the stock price."],
    correct:1, topic:"Contract Features", difficulty:"medium",
    rationale:"To maintain the economic neutrality of the option, the strike price is reduced by the amount of the special dividend.",
  },
  {
    id:94, text:'An investor buys a "Strangle" ($90 Put and $110 Call). If the stock price is $100 at expiration, what is the value of the position?',
    options:["$10","$5","$0","The value of the total premium paid."],
    correct:2, topic:"Moneyness & Intrinsic Value", difficulty:"medium",
    rationale:"At $100, the $90 put is OTM and the $110 call is OTM. Both expire worthless, so the position value is zero.",
  },
  {
    id:95, text:'A "Conversion Factor" is used in Bond Futures to:',
    options:["Convert US Dollars to Canadian Dollars.","Adjust the price of different deliverable bonds to a standard 6% (or equivalent) notional coupon.","Calculate the RR's commission.","Change a call option into a put option."],
    correct:1, topic:"Bond Futures", difficulty:"hard",
    rationale:"Since multiple bonds can be delivered, the conversion factor 'levels the playing field' based on the bond's actual coupon and maturity.",
  },
  {
    id:96, text:'"Physical Settlement" in an equity option means that upon exercise:',
    options:["The seller pays the buyer the difference in cash.","The clearinghouse delivers a physical gold bar.","The underlying shares are transferred from one account to another in exchange for the strike price.","The investor must visit the Montreal Exchange in person."],
    correct:2, topic:"Settlement", difficulty:"medium",
    rationale:"Most equity options result in the actual transfer of stock (shares).",
  },
  {
    id:97, text:"An option's Delta will be exactly 1.00 (or −1.00) only when:",
    options:["The option is at-the-money.","The option is deep-in-the-money and there is no time value remaining.","The option is deep-out-of-the-money.","The stock price is exactly equal to the strike price."],
    correct:1, topic:"The Greeks", difficulty:"hard",
    rationale:"At 1.00 Delta, the option behaves exactly like the stock. This only happens when the probability of the option expiring ITM is essentially 100%.",
  },
  {
    id:98, text:"An S&P/TSX 60 Index Future (SXF) is trading at 1,250. The multiplier is $200. If the Initial Margin is 10% of the notional value, what is the required deposit per contract?",
    options:["$1,250","$2,500","$25,000","$250,000"],
    correct:2, topic:"Numerical & Valuation", difficulty:"medium",
    rationale:"Notional Value = 1,250 × $200 = $250,000. 10% of $250,000 = $25,000.",
  },
  {
    id:99, text:'Which of the following defines a "Long Forward" position?',
    options:["The obligation to buy the asset at a future date.","The right to sell the asset at a future date.","An exchange-traded contract to buy gold.","A position that profits when volatility drops."],
    correct:0, topic:"Futures & Forwards", difficulty:"easy",
    rationale:"In a forward, 'Long' means you are the committed buyer at the agreed-upon price.",
  },
  {
    id:100, text:'"Negative Theta" means that the option\'s value ______ with each passing day.',
    options:["Increases","Stays the same","Decreases","Becomes zero immediately"],
    correct:2, topic:"The Greeks", difficulty:"easy",
    rationale:"Theta measures time decay; for option holders, time passing reduces the option's premium.",
  },
];

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TOPICS = [
  "Contract Features",
  "Rights & Obligations",
  "Futures & Forwards",
  "Settlement",
  "Moneyness & Intrinsic Value",
  "The Greeks",
  "Market Mechanics",
  "Numerical & Valuation",
  "Bond Futures",
];

const TOPIC_COLORS = {
  "Contract Features":        { bg:"#dbeafe", text:"#1e40af", bar:"#3b82f6" },
  "Rights & Obligations":     { bg:"#fce7f3", text:"#831843", bar:"#ec4899" },
  "Futures & Forwards":       { bg:"#ccfbf1", text:"#0e7490", bar:"#06b6d4" },
  "Settlement":               { bg:"#f0fdf4", text:"#14532d", bar:"#22c55e" },
  "Moneyness & Intrinsic Value":{ bg:"#fef9c3", text:"#713f12", bar:"#eab308" },
  "The Greeks":               { bg:"#ede9fe", text:"#5b21b6", bar:"#8b5cf6" },
  "Market Mechanics":         { bg:"#fee2e2", text:"#991b1b", bar:"#ef4444" },
  "Numerical & Valuation":    { bg:"#fef3c7", text:"#92400e", bar:"#f59e0b" },
  "Bond Futures":             { bg:"#f0f9ff", text:"#0c4a6e", bar:"#0ea5e9" },
};

const DIFF_COLORS = {
  easy:   { bg:"#dcfce7", text:"#166534", dot:"#22c55e" },
  medium: { bg:"#fef3c7", text:"#92400e", dot:"#f59e0b" },
  hard:   { bg:"#fee2e2", text:"#991b1b", dot:"#ef4444" },
};

const OPTION_LABELS = ["A","B","C","D"];
const QUIZ_PRESETS  = [
  { label:"Quick Test",  count:25,  mins:40  },
  { label:"Half Exam",   count:50,  mins:80  },
  { label:"Full Exam",   count:100, mins:160 },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const shuffle = arr => { const a=[...arr]; for(let i=a.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[a[i],a[j]]=[a[j],a[i]];} return a; };
const fmtTime = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
const calcScore = (ans,qs) => qs.filter((q,i) => ans[i]===q.correct).length;
const topicBreakdown = (ans,qs) => {
  const st = {}; TOPICS.forEach(t => { st[t]={total:0,correct:0}; });
  qs.forEach((q,i) => { if(!st[q.topic])st[q.topic]={total:0,correct:0}; st[q.topic].total++; if(ans[i]===q.correct)st[q.topic].correct++; });
  return st;
};
const diffBreakdown = (ans,qs) => {
  const st={easy:{t:0,c:0},medium:{t:0,c:0},hard:{t:0,c:0}};
  qs.forEach((q,i) => { st[q.difficulty].t++; if(ans[i]===q.correct)st[q.difficulty].c++; });
  return st;
};

// ─── DESIGN ───────────────────────────────────────────────────────────────────
const TEAL  = "#0d6f6f";
const TEAL2 = "#0f8a8a";
const AMBER = "#c9872b";

const S = {
  root: { minHeight:"100vh", background:"#f0f6f6", fontFamily:"Georgia,'Times New Roman',serif", color:"#0a1f1f" },
  card: { background:"#fff", borderRadius:16, border:"1px solid #cde0e0", boxShadow:"0 2px 14px rgba(0,80,80,0.07)", padding:28 },
  maxW: { maxWidth:900, margin:"0 auto", padding:"0 20px" },
  btnP: { background:`linear-gradient(135deg,${TEAL} 0%,${TEAL2} 100%)`, color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", cursor:"pointer", fontSize:14, fontWeight:600, letterSpacing:"0.03em", fontFamily:"Georgia,serif", transition:"all 0.2s", display:"inline-flex", alignItems:"center", gap:8 },
  btnG: { background:`linear-gradient(135deg,${AMBER} 0%,#e0a832 100%)`, color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", cursor:"pointer", fontSize:14, fontWeight:700, fontFamily:"Georgia,serif", transition:"all 0.2s", display:"inline-flex", alignItems:"center", gap:8 },
  btnO: { background:"transparent", color:"#4a7070", border:"1px solid #b8d4d4", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontSize:13, fontFamily:"Georgia,serif", transition:"all 0.2s", display:"inline-flex", alignItems:"center", gap:6 },
  badge:(bg,text) => ({ display:"inline-flex", alignItems:"center", gap:4, background:bg, color:text, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:600, letterSpacing:"0.05em", fontFamily:"Georgia,serif" }),
};

function useFonts() {
  useEffect(() => {
    const l=document.createElement("link"); l.rel="stylesheet";
    l.href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Source+Serif+4:ital,wght@0,300;0,400;0,600;1,400&family=JetBrains+Mono:wght@400;600&display=swap";
    document.head.appendChild(l);
  },[]);
}

// ─── BRAND ────────────────────────────────────────────────────────────────────
function Brand() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ width:36, height:36, borderRadius:8, background:`linear-gradient(135deg,${TEAL},${TEAL2})`, display:"flex", alignItems:"center", justifyContent:"center", color:AMBER, fontWeight:700, fontSize:14, flexShrink:0 }}>V</div>
      <div>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:TEAL, fontFamily:"Georgia,serif" }}>Vitta Finance Academy</div>
        <div style={{ fontSize:10, color:"#6b8f8f", fontFamily:"Georgia,serif" }}>CIRO Derivatives Exam Prep</div>
      </div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function Bar({ value, max, color=TEAL, thin=false }) {
  return (
    <div style={{ width:"100%", background:"#d8ecec", borderRadius:999, height:thin?4:7, overflow:"hidden" }}>
      <div style={{ width:`${Math.min(100,value/max*100)}%`, height:"100%", background:color, borderRadius:999, transition:"width 0.3s" }} />
    </div>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────
function HomeScreen({ onStart }) {
  return (
    <div style={{ minHeight:"100vh", background:"#f0f6f6" }}>
      <nav style={{ background:"#fff", borderBottom:"1px solid #cde0e0", padding:"16px 24px" }}>
        <div style={{ ...S.maxW, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <Brand />
          <div style={{ ...S.badge(TEAL,"#fff"), fontSize:12, padding:"6px 14px" }}>Derivatives Exam · Element 3</div>
        </div>
      </nav>

      <div style={{ ...S.maxW, padding:"56px 20px" }}>
        {/* Hero */}
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <div style={{ display:"inline-block", background:"linear-gradient(135deg,#e0f2f2,#cce8e8)", border:"1px solid #a8d0d0", borderRadius:40, padding:"6px 20px", fontSize:12, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#4a7070", marginBottom:20, fontFamily:"Georgia,serif" }}>
            Element 3 of 8
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:"clamp(30px,5vw,50px)", fontWeight:700, lineHeight:1.15, color:"#0a1f1f", margin:"0 0 16px" }}>
            Types &amp; Features<br/>of Derivatives
          </h1>
          <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:17, color:"#4a7070", maxWidth:560, margin:"0 auto 28px", lineHeight:1.7 }}>
            Options, futures, forwards, and swaps — contract mechanics, moneyness, the Greeks, settlement, and valuation.
          </p>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:24, flexWrap:"wrap" }}>
            {[["100","Questions"],["9","Topics"],["3","Difficulty Levels"]].map(([n,l]) => (
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:28, fontWeight:700, color:AMBER, lineHeight:1 }}>{n}</div>
                <div style={{ fontSize:11, color:"#6b8f8f", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"Georgia,serif" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mode cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:24, marginBottom:36 }}>
          {/* Practice */}
          <div onClick={() => onStart("practice")} style={{ ...S.card, cursor:"pointer", border:"2px solid transparent", transition:"all 0.25s" }}
            onMouseEnter={e => { e.currentTarget.style.border=`2px solid ${TEAL}`; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 10px 32px rgba(13,111,111,0.15)"; }}
            onMouseLeave={e => { e.currentTarget.style.border="2px solid transparent"; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 14px rgba(0,80,80,0.07)"; }}>
            <div style={{ fontSize:32, marginBottom:16 }}>📖</div>
            <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:700, margin:"0 0 10px", color:"#0a1f1f" }}>Practice Mode</h2>
            <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, color:"#4a7070", lineHeight:1.7, margin:"0 0 20px" }}>Work through all 100 questions at your own pace with immediate feedback and in-depth rationales.</p>
            {["Immediate answer feedback","Full rationale for every question","Flag questions for review","Topic & navigator panel"].map(f => (
              <div key={f} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:"#4a7070", fontFamily:"Georgia,serif", marginBottom:6 }}>
                <span style={{ color:"#22c55e", fontSize:14 }}>✓</span>{f}
              </div>
            ))}
            <div style={{ marginTop:20 }}>
              <button style={{ ...S.btnP, width:"100%", justifyContent:"center" }}>Start Practice →</button>
            </div>
          </div>

          {/* Timed Quiz */}
          <div onClick={() => onStart("quiz-config")} style={{ ...S.card, cursor:"pointer", border:"2px solid transparent", transition:"all 0.25s", background:"linear-gradient(160deg,#fff 70%,#fffbf0 100%)" }}
            onMouseEnter={e => { e.currentTarget.style.border=`2px solid ${AMBER}`; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 10px 32px rgba(201,135,43,0.18)"; }}
            onMouseLeave={e => { e.currentTarget.style.border="2px solid transparent"; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 14px rgba(0,80,80,0.07)"; }}>
            <div style={{ fontSize:32, marginBottom:16 }}>⏱</div>
            <h2 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:700, margin:"0 0 10px", color:"#0a1f1f" }}>Timed Quiz Mode</h2>
            <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, color:"#4a7070", lineHeight:1.7, margin:"0 0 20px" }}>Simulate real exam pressure with a countdown timer and full diagnostic dashboard on completion.</p>
            {["Randomized question order","Countdown timer","No hints until submission","Full diagnostic dashboard"].map(f => (
              <div key={f} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:"#4a7070", fontFamily:"Georgia,serif", marginBottom:6 }}>
                <span style={{ color:AMBER, fontSize:14 }}>✦</span>{f}
              </div>
            ))}
            <div style={{ marginTop:20 }}>
              <button style={{ ...S.btnG, width:"100%", justifyContent:"center" }}>Configure Quiz →</button>
            </div>
          </div>
        </div>

        {/* Topic chips */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, fontWeight:600, color:"#0a1f1f", margin:"0 0 14px" }}>Topics Covered</h3>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {TOPICS.map(t => {
              const c=TOPIC_COLORS[t]||{bg:"#f0f6f6",text:"#4a7070"};
              const count=QUESTIONS.filter(q=>q.topic===t).length;
              return (
                <div key={t} style={{ display:"flex", alignItems:"center", gap:6, background:c.bg, color:c.text, borderRadius:20, padding:"5px 12px", fontSize:12, fontFamily:"Georgia,serif", fontWeight:600 }}>
                  {t}<span style={{ opacity:0.7, fontWeight:400 }}>({count})</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ textAlign:"center", marginTop:44, color:"#8aa8a8", fontSize:12, fontFamily:"Georgia,serif" }}>
          Authored by Amina Beecroft · Vitta Finance Academy · vittafinance.ca
        </div>
      </div>
    </div>
  );
}

// ─── QUIZ CONFIG ──────────────────────────────────────────────────────────────
function QuizConfigScreen({ onBack, onStart }) {
  const [preset, setPreset] = useState(1);
  const p = QUIZ_PRESETS[preset];
  return (
    <div style={{ minHeight:"100vh", background:"#f0f6f6" }}>
      <nav style={{ background:"#fff", borderBottom:"1px solid #cde0e0", padding:"16px 24px" }}>
        <div style={{ ...S.maxW, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <Brand /><button onClick={onBack} style={S.btnO}>← Back</button>
        </div>
      </nav>
      <div style={{ ...S.maxW, padding:"56px 20px", maxWidth:600 }}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <h1 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:34, fontWeight:700, margin:"0 0 10px", color:"#0a1f1f" }}>Configure Your Quiz</h1>
          <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", color:"#4a7070", fontSize:16 }}>Questions drawn randomly from the full 100-question bank.</p>
        </div>
        <div style={{ ...S.card, marginBottom:22 }}>
          <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:"0 0 16px", color:"#0a1f1f" }}>Select Quiz Length</h3>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            {QUIZ_PRESETS.map((pr,i) => (
              <div key={i} onClick={() => setPreset(i)} style={{ flex:1, minWidth:100, border:preset===i?`2px solid ${TEAL}`:"1.5px solid #cde0e0", borderRadius:12, padding:"16px 12px", textAlign:"center", cursor:"pointer", background:preset===i?"#e8f5f5":"#fff", transition:"all 0.2s" }}>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:26, fontWeight:700, color:preset===i?TEAL:"#4a7070" }}>{pr.count}</div>
                <div style={{ fontSize:11, color:"#4a7070", fontFamily:"Georgia,serif", marginBottom:4 }}>questions</div>
                <div style={{ ...S.badge(preset===i?TEAL:"#f0f6f6",preset===i?"#fff":"#4a7070"), fontSize:10, padding:"2px 8px" }}>{pr.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...S.card, marginBottom:28 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
            {[["⏱","Time Limit",`${p.mins} minutes`],["⚡","Avg per Q",`~${Math.round(p.mins*60/p.count)}s`],["📊","Coverage",p.count===100?"Full bank":`${p.count} random Q`],["🎯","Pass Mark","≥ 60% recommended"]].map(([icon,label,val]) => (
              <div key={label} style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <span style={{ fontSize:22 }}>{icon}</span>
                <div>
                  <div style={{ fontSize:11, color:"#8aa8a8", fontFamily:"Georgia,serif", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</div>
                  <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:16, fontWeight:600, color:"#0a1f1f" }}>{val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => onStart(p)} style={{ ...S.btnG, width:"100%", justifyContent:"center", padding:"16px", fontSize:15 }}>Begin Timed Quiz →</button>
      </div>
    </div>
  );
}

// ─── QUESTION CARD ────────────────────────────────────────────────────────────
function QCard({ q, qNum, total, selectedIndex, revealed, onSelect, onReveal, onFlag, isFlagged, mode }) {
  const tc=TOPIC_COLORS[q.topic]||{bg:"#f0f6f6",text:"#4a7070"};
  const dc=DIFF_COLORS[q.difficulty];
  return (
    <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
      <div style={{ padding:"18px 28px 14px", borderBottom:"1px solid #eaf4f4", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:"#8aa8a8", fontWeight:600 }}>{String(qNum).padStart(2,"0")} / {String(total).padStart(2,"0")}</span>
          <div style={S.badge(tc.bg,tc.text)}>{q.topic}</div>
          <div style={S.badge(dc.bg,dc.text)}><span style={{ color:dc.dot, fontSize:8 }}>●</span>{q.difficulty.charAt(0).toUpperCase()+q.difficulty.slice(1)}</div>
        </div>
        <button onClick={() => onFlag(q.id)} style={{ ...S.btnO, border:isFlagged?"1.5px solid #f59e0b":"1px solid #cde0e0", color:isFlagged?"#d97706":"#8aa8a8", padding:"5px 12px", fontSize:12, background:isFlagged?"#fef9ee":"transparent" }}>
          {isFlagged?"🚩 Flagged":"⚑ Flag"}
        </button>
      </div>
      <div style={{ padding:"24px 28px 20px" }}>
        <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:16, lineHeight:1.75, color:"#0a1f1f", margin:0 }}>{q.text}</p>
      </div>
      <div style={{ padding:"0 28px 24px", display:"flex", flexDirection:"column", gap:10 }}>
        {q.options.map((opt,i) => {
          let bg="#fff",border="1.5px solid #cde0e0",textColor="#0a1f1f",lBg="#e8f4f4",lColor="#4a7070";
          if(revealed&&mode==="practice"){
            if(i===q.correct){bg="#f0fdf4";border="1.5px solid #86efac";textColor="#166534";lBg="#22c55e";lColor="#fff";}
            else if(i===selectedIndex){bg="#fef2f2";border="1.5px solid #fca5a5";textColor="#991b1b";lBg="#ef4444";lColor="#fff";}
          } else if(i===selectedIndex){bg="#eaf6f6";border=`1.5px solid ${TEAL}`;textColor=TEAL;lBg=TEAL;lColor="#fff";}
          return (
            <div key={i} onClick={() => !revealed&&onSelect(i)}
              style={{ display:"flex", alignItems:"flex-start", gap:12, background:bg, border, borderRadius:10, padding:"13px 16px", cursor:revealed?"default":"pointer", transition:"all 0.15s" }}
              onMouseEnter={e => { if(!revealed&&i!==selectedIndex){e.currentTarget.style.border=`1.5px solid ${TEAL}`;e.currentTarget.style.background="#f4fbfb";} }}
              onMouseLeave={e => { if(!revealed&&i!==selectedIndex){e.currentTarget.style.border="1.5px solid #cde0e0";e.currentTarget.style.background="#fff";} }}>
              <span style={{ flexShrink:0, width:26, height:26, borderRadius:6, background:lBg, color:lColor, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", transition:"all 0.15s" }}>{OPTION_LABELS[i]}</span>
              <span style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14.5, lineHeight:1.6, color:textColor, paddingTop:3 }}>
                {opt}
                {revealed&&mode==="practice"&&i===q.correct&&<span style={{ marginLeft:8, fontSize:13, color:"#22c55e" }}>✓ Correct</span>}
                {revealed&&mode==="practice"&&i===selectedIndex&&i!==q.correct&&<span style={{ marginLeft:8, fontSize:13, color:"#ef4444" }}>✗ Incorrect</span>}
              </span>
            </div>
          );
        })}
      </div>
      {mode==="practice" && (
        <div style={{ padding:"0 28px 24px" }}>
          {!revealed ? (
            <button onClick={onReveal} disabled={selectedIndex==null||selectedIndex===undefined}
              style={{ ...S.btnP, opacity:(selectedIndex!=null&&selectedIndex!==undefined)?1:0.4, cursor:(selectedIndex!=null&&selectedIndex!==undefined)?"pointer":"not-allowed", padding:"10px 24px", fontSize:13 }}>
              Check Answer
            </button>
          ) : (
            <div style={{ background:"#fffbea", border:"1px solid #fde68a", borderRadius:10, padding:"14px 18px" }}>
              <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:13, fontWeight:700, color:"#92400e", marginBottom:8 }}>💡 Rationale</div>
              <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, lineHeight:1.7, color:"#451a03", margin:0 }}>{q.rationale}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PRACTICE MODE ────────────────────────────────────────────────────────────
function PracticeMode({ onExit, onFinish }) {
  const [current,setCurrent]=useState(0);
  const [answers,setAnswers]=useState({});
  const [revealed,setRevealed]=useState({});
  const [flagged,setFlagged]=useState(new Set());
  const [filter,setFilter]=useState("all");
  const [showNav,setShowNav]=useState(false);

  const qs=useMemo(() => {
    if(filter==="flagged") return QUESTIONS.filter(q=>flagged.has(q.id));
    if(filter==="incorrect") return QUESTIONS.filter((q,i)=>revealed[i]&&answers[i]!==q.correct);
    return QUESTIONS;
  },[filter,flagged,revealed,answers]);

  const q=qs[current]||QUESTIONS[0];
  const gi=QUESTIONS.indexOf(q);
  const answered=Object.keys(answers).length;
  const correct=QUESTIONS.filter((q,i)=>answers[i]===q.correct).length;

  return (
    <div style={{ minHeight:"100vh", background:"#f0f6f6" }}>
      <div style={{ position:"sticky", top:0, zIndex:100, background:"#fff", borderBottom:"1px solid #cde0e0", padding:"12px 20px", boxShadow:"0 2px 12px rgba(0,80,80,0.06)" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <Brand />
          <div style={{ flex:1, minWidth:140 }}>
            <div style={{ fontSize:10, color:"#8aa8a8", fontFamily:"Georgia,serif", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>Progress · {answered}/{QUESTIONS.length} · {correct} correct</div>
            <Bar value={answered} max={QUESTIONS.length} />
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button onClick={() => setShowNav(!showNav)} style={{ ...S.btnO, fontSize:12 }}>☰ Navigator</button>
            {answered===QUESTIONS.length&&<button onClick={() => onFinish({answers,mode:"practice"})} style={{ ...S.btnP, padding:"8px 16px", fontSize:12 }}>View Results</button>}
            <button onClick={onExit} style={{ ...S.btnO, fontSize:12, color:"#ef4444", borderColor:"#fca5a5" }}>✕ Exit</button>
          </div>
        </div>
        {showNav&&(
          <div style={{ maxWidth:900, margin:"12px auto 0", background:"#f4fbfb", borderRadius:12, border:"1px solid #cde0e0", padding:16 }}>
            <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
              {["all","flagged","incorrect"].map(f => (
                <button key={f} onClick={() => {setFilter(f);setCurrent(0);}} style={{ ...S.btnO, background:filter===f?TEAL:"transparent", color:filter===f?"#fff":"#4a7070", fontSize:11, padding:"5px 12px", borderColor:filter===f?TEAL:"#cde0e0" }}>
                  {f==="all"?`All (${QUESTIONS.length})`:f==="flagged"?`Flagged (${flagged.size})`:`Incorrect (${QUESTIONS.filter((q,i)=>revealed[i]&&answers[i]!==q.correct).length})`}
                </button>
              ))}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {qs.map((q,i) => {
                const gi2=QUESTIONS.indexOf(q);
                const isRev=revealed[gi2],isCorr=answers[gi2]===q.correct,isFlag=flagged.has(q.id);
                let bg="#e8f4f4",color="#4a7070",border="1px solid #cde0e0";
                if(isFlag){bg="#fef9ee";color="#d97706";border="1px solid #fde68a";}
                if(isRev){bg=isCorr?"#dcfce7":"#fee2e2";color=isCorr?"#166534":"#991b1b";border=`1px solid ${isCorr?"#86efac":"#fca5a5"}`;}
                if(i===current)border=`2px solid ${TEAL}`;
                return (
                  <button key={q.id} onClick={() => {setCurrent(i);setShowNav(false);}} style={{ width:34, height:34, borderRadius:7, background:bg, color, border, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:"'JetBrains Mono',monospace", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {q.id}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ ...S.maxW, padding:"28px 20px" }}>
        {qs.length===0 ? (
          <div style={{ ...S.card, textAlign:"center", padding:48 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:20 }}>No questions in this filter</h3>
            <button onClick={() => setFilter("all")} style={S.btnP}>View All Questions</button>
          </div>
        ) : (
          <>
            <QCard q={q} qNum={current+1} total={qs.length} selectedIndex={answers[gi]} revealed={!!revealed[gi]}
              onSelect={i => setAnswers(a => ({...a,[gi]:i}))}
              onReveal={() => setRevealed(r => ({...r,[gi]:true}))}
              onFlag={id => setFlagged(f => { const n=new Set(f); n.has(id)?n.delete(id):n.add(id); return n; })}
              isFlagged={flagged.has(q.id)} mode="practice" />
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:20, flexWrap:"wrap", gap:12 }}>
              <button onClick={() => setCurrent(c => Math.max(0,c-1))} disabled={current===0} style={{ ...S.btnO, opacity:current===0?0.4:1 }}>← Previous</button>
              <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, color:"#8aa8a8" }}>{current+1} of {qs.length}</span>
              {current<qs.length-1
                ? <button onClick={() => setCurrent(c => c+1)} style={S.btnP}>Next →</button>
                : <button onClick={() => onFinish({answers,mode:"practice"})} style={S.btnG}>View Results →</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── QUIZ MODE ────────────────────────────────────────────────────────────────
function QuizMode({ config, onExit, onFinish }) {
  const quizQs=useMemo(() => shuffle([...Array(100)].map((_,i)=>i)).slice(0,config.count).map(i=>QUESTIONS[i]),[config.count]);
  const [current,setCurrent]=useState(0);
  const [answers,setAnswers]=useState({});
  const [timeLeft,setTimeLeft]=useState(config.mins*60);
  const [confirm,setConfirm]=useState(false);
  const timerRef=useRef(null);
  const tlRef=useRef(config.mins*60);

  const doSubmit=useCallback(() => {
    clearInterval(timerRef.current);
    onFinish({answers,quizQs,mode:"quiz",timeUsed:config.mins*60-tlRef.current,timeLimit:config.mins*60});
  },[answers,quizQs,config,onFinish]);

  useEffect(() => {
    timerRef.current=setInterval(() => {
      setTimeLeft(t => { const n=t-1; tlRef.current=n; if(n<=0){clearInterval(timerRef.current);doSubmit();return 0;} return n; });
    },1000);
    return () => clearInterval(timerRef.current);
  },[doSubmit]);

  const answered=Object.keys(answers).length;
  const isLow=timeLeft<300;
  const q=quizQs[current];

  return (
    <div style={{ minHeight:"100vh", background:"#f0f6f6" }}>
      <div style={{ position:"sticky", top:0, zIndex:100, background:"#fff", borderBottom:"1px solid #cde0e0", padding:"12px 20px", boxShadow:"0 2px 12px rgba(0,80,80,0.06)" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <Brand />
          <div style={{ display:"flex", alignItems:"center", gap:8, background:isLow?"#fef2f2":"#e8f4f4", border:`1.5px solid ${isLow?"#fca5a5":"#a8d0d0"}`, borderRadius:8, padding:"6px 14px" }}>
            <span style={{ fontSize:14 }}>{isLow?"🔴":"⏱"}</span>
            <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:20, fontWeight:700, color:isLow?"#dc2626":timeLeft<600?"#d97706":"#0a1f1f", letterSpacing:"0.05em" }}>{fmtTime(timeLeft)}</span>
          </div>
          <div style={{ flex:1, minWidth:120 }}>
            <div style={{ fontSize:10, color:"#8aa8a8", fontFamily:"Georgia,serif", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>{answered}/{quizQs.length} answered</div>
            <Bar value={answered} max={quizQs.length} />
          </div>
          <button onClick={() => setConfirm(true)} style={{ ...S.btnP, padding:"8px 16px", fontSize:12 }}>Submit Quiz</button>
          <button onClick={onExit} style={{ ...S.btnO, fontSize:12, color:"#ef4444" }}>✕</button>
        </div>
        <div style={{ maxWidth:900, margin:"4px auto 0" }}><Bar value={current+1} max={quizQs.length} color={AMBER} thin /></div>
      </div>

      {confirm&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999, padding:20 }}>
          <div style={{ ...S.card, maxWidth:400, width:"100%", textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:20, margin:"0 0 8px" }}>Submit Quiz?</h3>
            <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", color:"#4a7070", fontSize:14, margin:"0 0 20px" }}>
              You've answered <strong>{answered}</strong> of <strong>{quizQs.length}</strong>.
              {answered<quizQs.length&&` ${quizQs.length-answered} unanswered will be marked incorrect.`}
            </p>
            <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
              <button onClick={() => setConfirm(false)} style={S.btnO}>Keep Going</button>
              <button onClick={doSubmit} style={S.btnG}>Submit &amp; See Results</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ ...S.maxW, padding:"28px 20px" }}>
        <QCard q={q} qNum={current+1} total={quizQs.length} selectedIndex={answers[current]} revealed={false}
          onSelect={i => setAnswers(a => ({...a,[current]:i}))} onReveal={() => {}} onFlag={() => {}} isFlagged={false} mode="quiz" />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:20, flexWrap:"wrap", gap:12 }}>
          <button onClick={() => setCurrent(c => Math.max(0,c-1))} disabled={current===0} style={{ ...S.btnO, opacity:current===0?0.4:1 }}>← Previous</button>
          <div style={{ display:"flex", gap:4, flexWrap:"wrap", maxWidth:400, justifyContent:"center" }}>
            {quizQs.map((_,i) => (
              <div key={i} onClick={() => setCurrent(i)} style={{ width:i===current?16:8, height:8, borderRadius:4, cursor:"pointer", background:i===current?TEAL:answers[i]!==undefined?AMBER:"#b8d4d4", transition:"all 0.2s" }} />
            ))}
          </div>
          {current<quizQs.length-1
            ? <button onClick={() => setCurrent(c => c+1)} style={S.btnP}>Next →</button>
            : <button onClick={() => setConfirm(true)} style={S.btnG}>Submit →</button>}
        </div>
      </div>
    </div>
  );
}

// ─── RESULTS ──────────────────────────────────────────────────────────────────
function ResultsScreen({ data, onRetry, onHome }) {
  const { answers, mode, timeUsed } = data;
  const questionList=data.quizQs||QUESTIONS;
  const [rf,setRf]=useState("all");
  const [exp,setExp]=useState(null);

  const total=questionList.length;
  const correct=calcScore(answers,questionList);
  const pct=Math.round(correct/total*100);
  const passed=pct>=60;
  const ts=useMemo(() => topicBreakdown(answers,questionList),[answers,questionList]);
  const ds=useMemo(() => diffBreakdown(answers,questionList),[answers,questionList]);

  const reviewQs=useMemo(() =>
    questionList.map((q,i) => ({q,i,selected:answers[i],isCorrect:answers[i]===q.correct}))
      .filter(x => rf==="incorrect"?!x.isCorrect:rf==="correct"?x.isCorrect:true),
    [questionList,answers,rf]);

  const sc=pct>=75?"#166534":pct>=60?"#92400e":"#991b1b";
  const sb=pct>=75?"#dcfce7":pct>=60?"#fef3c7":"#fee2e2";

  return (
    <div style={{ minHeight:"100vh", background:"#f0f6f6" }}>
      <nav style={{ background:"#fff", borderBottom:"1px solid #cde0e0", padding:"16px 24px" }}>
        <div style={{ ...S.maxW, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <Brand />
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onRetry} style={S.btnO}>↺ Try Again</button>
            <button onClick={onHome} style={S.btnP}>⌂ Home</button>
          </div>
        </div>
      </nav>

      <div style={{ ...S.maxW, padding:"32px 20px" }}>
        {/* Score hero */}
        <div style={{ ...S.card, marginBottom:24, textAlign:"center", padding:"40px 28px" }}>
          <div style={{ display:"inline-block", background:sb, color:sc, borderRadius:20, padding:"4px 16px", fontSize:12, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"Georgia,serif", marginBottom:16 }}>
            {passed?"✓ Pass":"✗ Needs Review"} · Element 3: Types &amp; Features
          </div>
          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:"clamp(56px,10vw,80px)", fontWeight:700, color:sc, lineHeight:1, marginBottom:8 }}>{pct}%</div>
          <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", color:"#4a7070", fontSize:16, marginBottom:24 }}>{correct} correct out of {total} questions</div>
          <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap" }}>
            {[["✓",String(correct),"Correct","#166534","#dcfce7"],["✗",String(total-correct),"Incorrect","#991b1b","#fee2e2"],
              ...(mode==="quiz"&&timeUsed?[["⏱",fmtTime(timeUsed),"Time Used","#1e40af","#dbeafe"],["⚡",`${Math.round(timeUsed/total)}s`,"Avg / Q","#92400e","#fef3c7"]]:[])
            ].map(([icon,val,label,tc,bg]) => (
              <div key={label} style={{ background:bg, color:tc, borderRadius:12, padding:"14px 20px", minWidth:90, textAlign:"center" }}>
                <div style={{ fontSize:20 }}>{icon}</div>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:700 }}>{val}</div>
                <div style={{ fontSize:11, fontFamily:"Georgia,serif", opacity:0.8 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Breakdown */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20, marginBottom:24 }}>
          <div style={S.card}>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:"0 0 20px", color:"#0a1f1f" }}>Performance by Topic</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {TOPICS.filter(t => ts[t]&&ts[t].total>0).map(t => {
                const st=ts[t], tp=Math.round(st.correct/st.total*100), c=TOPIC_COLORS[t]||{bar:"#8aa8a8"};
                return (
                  <div key={t}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:12, fontFamily:"Georgia,serif", color:"#0a1f1f", fontWeight:600 }}>{t}</span>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:tp>=60?"#166534":"#991b1b", fontWeight:700 }}>{st.correct}/{st.total} ({tp}%)</span>
                    </div>
                    <div style={{ background:"#d8ecec", borderRadius:999, height:8, overflow:"hidden" }}>
                      <div style={{ width:`${tp}%`, height:"100%", background:c.bar, borderRadius:999, transition:"width 0.8s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={S.card}>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:"0 0 20px", color:"#0a1f1f" }}>Performance by Difficulty</h3>
            {[["easy","Easy"],["medium","Medium"],["hard","Hard"]].map(([key,label]) => {
              const st=ds[key], tp=st.t>0?Math.round(st.c/st.t*100):0, dc=DIFF_COLORS[key];
              return (
                <div key={key} style={{ marginBottom:18 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ color:dc.dot, fontSize:12 }}>●</span>
                      <span style={{ fontSize:13, fontFamily:"Georgia,serif", color:"#0a1f1f", fontWeight:600 }}>{label}</span>
                    </div>
                    <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:tp>=60?"#166534":"#991b1b", fontWeight:700 }}>{st.c}/{st.t} ({tp}%)</span>
                  </div>
                  <div style={{ background:"#d8ecec", borderRadius:999, height:10, overflow:"hidden" }}>
                    <div style={{ width:`${tp}%`, height:"100%", background:dc.dot, borderRadius:999, transition:"width 0.8s ease" }} />
                  </div>
                </div>
              );
            })}
            {ds.hard.t>0&&ds.hard.c/ds.hard.t<0.6&&(
              <div style={{ background:"#fffbea", border:"1px solid #fde68a", borderRadius:10, padding:"12px 14px", marginTop:8 }}>
                <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color:"#92400e", margin:0, lineHeight:1.6 }}>
                  💡 Focus on <strong>Hard</strong> questions — you scored {Math.round(ds.hard.c/ds.hard.t*100)}% in that category.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Review */}
        <div style={S.card}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
            <h3 style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:17, margin:0, color:"#0a1f1f" }}>Question Review</h3>
            <div style={{ display:"flex", gap:8 }}>
              {["all","incorrect","correct"].map(f => (
                <button key={f} onClick={() => setRf(f)} style={{ ...S.btnO, background:rf===f?TEAL:"transparent", color:rf===f?"#fff":"#4a7070", borderColor:rf===f?TEAL:"#cde0e0", fontSize:11, padding:"5px 12px" }}>
                  {f==="all"?`All (${total})`:f==="incorrect"?`Incorrect (${total-correct})`:`Correct (${correct})`}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {reviewQs.map(({ q,i,selected,isCorrect }) => (
              <div key={q.id} style={{ border:`1px solid ${isCorrect?"#86efac":"#fca5a5"}`, borderRadius:10, background:isCorrect?"#f0fdf4":"#fef2f2", overflow:"hidden" }}>
                <div onClick={() => setExp(exp===q.id?null:q.id)} style={{ padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}>
                  <span style={{ flexShrink:0, fontSize:16, marginTop:1 }}>{selected===undefined?"○":isCorrect?"✓":"✗"}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"#8aa8a8" }}>Q{q.id}</span>
                      <div style={S.badge(TOPIC_COLORS[q.topic]?.bg||"#e8f4f4",TOPIC_COLORS[q.topic]?.text||"#4a7070")}>{q.topic}</div>
                      <div style={S.badge(DIFF_COLORS[q.difficulty].bg,DIFF_COLORS[q.difficulty].text)}>{q.difficulty}</div>
                    </div>
                    <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color:"#0a1f1f", margin:0, lineHeight:1.5 }}>{q.text.length>120?q.text.slice(0,120)+"…":q.text}</p>
                  </div>
                  <span style={{ flexShrink:0, color:"#8aa8a8", fontSize:12 }}>{exp===q.id?"▲":"▼"}</span>
                </div>
                {exp===q.id&&(
                  <div style={{ padding:"0 16px 16px 44px" }}>
                    <p style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13.5, color:"#0a1f1f", margin:"0 0 10px", lineHeight:1.7 }}>{q.text}</p>
                    {q.options.map((opt,oi) => {
                      let bg="#fff",border="1px solid #cde0e0",color="#0a1f1f";
                      if(oi===q.correct){bg="#f0fdf4";border="1px solid #86efac";color="#166534";}
                      else if(oi===selected){bg="#fef2f2";border="1px solid #fca5a5";color="#991b1b";}
                      return (
                        <div key={oi} style={{ display:"flex", gap:8, background:bg, border, borderRadius:8, padding:"8px 12px", marginBottom:6 }}>
                          <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color, fontWeight:700, flexShrink:0 }}>{OPTION_LABELS[oi]}</span>
                          <span style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color, lineHeight:1.5 }}>
                            {opt}{oi===q.correct&&<span style={{ marginLeft:6, fontSize:11 }}>✓ Correct Answer</span>}{oi===selected&&oi!==q.correct&&<span style={{ marginLeft:6, fontSize:11 }}>✗ Your Answer</span>}
                          </span>
                        </div>
                      );
                    })}
                    <div style={{ background:"#fffbea", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginTop:8 }}>
                      <span style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:12, fontWeight:700, color:"#92400e" }}>💡 Rationale:</span>
                      <span style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:13, color:"#451a03", marginLeft:8, lineHeight:1.6 }}>{q.rationale}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign:"center", marginTop:44, color:"#8aa8a8", fontSize:12, fontFamily:"Georgia,serif" }}>
          Vitta Finance Academy · Element 3 of 8 · vittafinance.ca
        </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  useFonts();
  const [screen,setScreen]=useState("home");
  const [cfg,setCfg]=useState(null);
  const [results,setResults]=useState(null);
  return (
    <div style={S.root}>
      {screen==="home"&&<HomeScreen onStart={m => setScreen(m==="practice"?"practice":"quiz-config")} />}
      {screen==="practice"&&<PracticeMode onExit={() => setScreen("home")} onFinish={d => {setResults(d);setScreen("results");}} />}
      {screen==="quiz-config"&&<QuizConfigScreen onBack={() => setScreen("home")} onStart={c => {setCfg(c);setScreen("quiz");}} />}
      {screen==="quiz"&&cfg&&<QuizMode config={cfg} onExit={() => setScreen("home")} onFinish={d => {setResults(d);setScreen("results");}} />}
      {screen==="results"&&results&&<ResultsScreen data={results} onRetry={() => {setResults(null);setScreen(results?.mode==="quiz"?"quiz-config":"home");}} onHome={() => {setResults(null);setScreen("home");}} />}
    </div>
  );
}
